import React, { useEffect } from 'react';
import { BookOpen, X, Search, ChevronDown } from 'lucide-react';
import { useResponsive } from '../hooks/useResponsive';

const GUIDE_SECTIONS = [
    {
        title: 'Getting Started',
        faqs: [
            {
                question: 'What is Recruiter360?',
                answer: 'Recruiter360 is a recruitment management dashboard used to manage job requirements, tasks, candidates, interviews, and recruitment progress.'
            },
            {
                question: 'What are the different user roles?',
                answer: 'Recruiter: Manages assigned recruitment tasks and candidates. Team Lead: Oversees recruitment activities and manages permitted tasks. Admin: Has administrative access to the application.'
            },
            {
                question: 'How do I log out?',
                answer: 'Click the Logout button in the dashboard header to end your current session.'
            }
        ]
    },
    {
        title: 'Tasks',
        faqs: [
            {
                question: 'What do the task statuses mean?',
                answer: 'Assigned: A task has been assigned. WIP: Work is in progress. Ready To Submit: The task is ready for review. Completed: The task has been approved. Pending: The task is awaiting acceptance or further action.'
            },
            {
                question: 'How do I start a Pending task?',
                answer: 'Open your Tasks section and use Accept Task, when available, to move the task into Work in Progress (WIP).'
            },
            {
                question: 'How is a task completed?',
                answer: 'Move the task through the permitted workflow until it reaches Ready To Submit. Completion occurs after the authorised approval process.'
            },
            {
                question: 'Can I reassign a task?',
                answer: 'Task reassignment depends on your role, task status, and the permissions configured by the application.'
            }
        ]
    },
    {
        title: 'Job Gathering',
        faqs: [
            {
                question: 'What information is required for a job requirement?',
                answer: 'Job requirements include the company, role type, job role, experience, location, candidate budget, and Evanti SPOC, along with the applicable dates.'
            },
            {
                question: 'Can I change a Job ID after creating a requirement?',
                answer: 'The Job ID and Date Received are locked after creation to preserve the original job requirement details.'
            }
        ]
    },
    {
        title: 'Candidates and Interviews',
        faqs: [
            {
                question: 'Where can I find candidate details?',
                answer: 'Open the relevant recruitment task or candidate details section available in your dashboard.'
            },
            {
                question: 'How do I add or update an interview link?',
                answer: 'Open the relevant Interviews section and use the available editing controls. Changes are subject to your role permissions.'
            },
            {
                question: 'Can I view interview link change history?',
                answer: 'Where change-history controls are available, use the history icon to review recorded updates and their timestamps.'
            }
        ]
    },
    {
        title: 'Troubleshooting',
        faqs: [
            {
                question: 'Why can’t I see a particular feature?',
                answer: 'Features depend on your assigned role and permissions. Contact your Team Lead or Admin if you believe you need access.'
            },
            {
                question: 'What should I do if a page or task does not load?',
                answer: 'Refresh the page and check your connection. If the issue continues, contact your Admin and provide the affected page and a description of the problem.'
            }
        ]
    }
];

const GuideModal = ({ onClose }) => {
    const [search, setSearch] = React.useState('');
    const [expanded, setExpanded] = React.useState('Getting Started-0');
    const { width } = useResponsive();
    const isNarrow = width <= 767; // matches the 767/768 breakpoint spec exactly
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    const query = search.trim().toLowerCase();

    const filteredSections = GUIDE_SECTIONS.map((section) => ({
        ...section,
        faqs: section.faqs.filter((faq) =>
            !query ||
            section.title.toLowerCase().includes(query) ||
            faq.question.toLowerCase().includes(query) ||
            faq.answer.toLowerCase().includes(query)
        )
    })).filter((section) => section.faqs.length > 0);

    return (
        <div
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
            style={{
                position: 'fixed', inset: 0, zIndex: 10000,
                background: 'rgba(15, 23, 42, 0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px', backdropFilter: 'blur(3px)'
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="guide-title"
                style={{
                    width: '100%', maxWidth: '680px',
                    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                    background: '#fff', borderRadius: '16px',
                    boxShadow: '0 24px 70px rgba(0,0,0,0.25)',
                    overflow: 'hidden'
                }}
            >
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', padding: '20px 24px',
                    borderBottom: '1px solid #e5e7eb',
                    background: '#fafaff'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '42px', height: '42px', borderRadius: '12px',
                            background: '#f0f9ff', color: '#0369a1',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <img
                                src="/favicon.ico"
                                alt="Favicon Logo"
                                style={{
                                    width: isNarrow ? '100px' : '150px',
                                    height: isNarrow ? '30px' : '45px',
                                    objectFit: 'contain'
                                }}
                            />
                            {/* <BookOpen size={22} /> */}
                        </div>
                        <div>
                            <h2 id="guide-title" style={{
                                margin: 0, fontSize: '20px', color: '#111827'
                            }}>
                                Recruiter360 Guide
                            </h2>
                            <p style={{
                                margin: '4px 0 0', fontSize: '13px', color: '#6b7280'
                            }}>
                                Help, FAQs and workflow instructions
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close guide"
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: '36px', height: '36px', border: 'none',
                            borderRadius: '8px', background: '#f3f4f6',
                            color: '#374151', cursor: 'pointer', flexShrink: 0
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div style={{ padding: '16px 24px 12px' }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        border: '1px solid #d1d5db', borderRadius: '10px',
                        padding: '0 12px', background: '#fff'
                    }}>
                        <Search size={18} color="#9ca3af" />
                        <input
                            type="search"
                            placeholder="Search questions and answers..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            style={{
                                width: '100%', padding: '12px 0',
                                border: 'none', outline: 'none',
                                fontSize: '14px', color: '#111827',
                                background: 'transparent'
                            }}
                        />
                    </div>
                </div>

                <div style={{
                    overflowY: 'auto', padding: '4px 24px 24px',
                    overscrollBehavior: 'contain'
                }}>
                    {filteredSections.length === 0 ? (
                        <div style={{
                            padding: '36px 12px', textAlign: 'center', color: '#6b7280'
                        }}>
                            <Search size={28} style={{ marginBottom: '10px' }} />
                            <p style={{ margin: 0, fontWeight: '600' }}>
                                No matching questions found
                            </p>
                            <p style={{ fontSize: '13px' }}>
                                Try a different keyword.
                            </p>
                        </div>
                    ) : (
                        filteredSections.map((section) => (
                            <section key={section.title} style={{ marginTop: '16px' }}>
                                <h3 style={{
                                    margin: '0 0 10px', fontSize: '12px',
                                    fontWeight: '800', letterSpacing: '0.08em',
                                    textTransform: 'uppercase', color: '#7c3aed'
                                }}>
                                    {section.title}
                                </h3>

                                {section.faqs.map((faq) => {
                                    const id = `${section.title}-${faq.question}`;
                                    const isOpen = expanded === id || Boolean(query);

                                    return (
                                        <div key={id} style={{
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '10px', marginBottom: '8px',
                                            overflow: 'hidden'
                                        }}>
                                            <button
                                                type="button"
                                                onClick={() => setExpanded(
                                                    expanded === id ? '' : id
                                                )}
                                                aria-expanded={isOpen}
                                                style={{
                                                    width: '100%', display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: '12px', textAlign: 'left',
                                                    padding: '14px 16px',
                                                    border: 'none', background: '#fff',
                                                    cursor: 'pointer', color: '#1f2937',
                                                    fontSize: '14px', fontWeight: '600'
                                                }}
                                            >
                                                <span>{faq.question}</span>
                                                <ChevronDown
                                                    size={17}
                                                    style={{
                                                        flexShrink: 0,
                                                        transform: isOpen ? 'rotate(180deg)' : 'none',
                                                        transition: 'transform 0.2s'
                                                    }}
                                                />
                                            </button>

                                            {isOpen && (
                                                <div style={{
                                                    padding: '0 16px 15px',
                                                    color: '#4b5563', fontSize: '13px',
                                                    lineHeight: 1.7, background: '#fff'
                                                }}>
                                                    {faq.answer}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </section>
                        ))
                    )}
                </div>

                <div style={{
                    padding: '12px 24px', borderTop: '1px solid #e5e7eb',
                    color: '#6b7280', fontSize: '12px', textAlign: 'center'
                }}>
                    Recruiter360 · Quick Help Guide
                </div>
            </div>
        </div>
    );
};

export default GuideModal;